// Diagnostics battery (IM-2465). Client-side only — no new backend endpoint.
// Each check is best-effort and never throws to the caller; failures are
// captured as a 'fail'/'warn' result so one broken probe can't abort the run.
//
// Credentials are NEVER placed in a result: the WS URL token and the REST
// Authorization header are used for probing but redacted from every detail.

import type {
  CheckDetail,
  CheckResult,
  CheckStatus,
  DiagnosticsContext,
  Verdict,
} from './diagnostics-types';

// Backend configuration, resolved from the same env vars the app uses:
// REST  -> auth-service.tsx / use-requests.ts (REACT_APP_AUTH_DOMAIN)
// WS    -> websocket-provider.tsx (REACT_APP_WEBSOCKET_URL)
export const REST_BASE_URL =
  import.meta.env.REACT_APP_AUTH_DOMAIN ?? 'https://auth.test.immersive-finance.com';
export const WS_BASE_URL = import.meta.env.REACT_APP_WEBSOCKET_URL ?? '';

// A cheap authenticated GET that exists today; used both for the request-phase
// breakdown and as the REST reachability target. No dedicated health endpoint
// exists yet. TODO(IM-2471): real /health endpoint.
const REST_PROBE_PATH = '/private/verify_token';

const nowMs = (): number => performance.now();
const round1 = (v: number): number => Math.round(v * 10) / 10;

const errMessage = (err: unknown): string => (err instanceof Error ? err.message : String(err));

// ---------------------------------------------------------------------------
// 1. Client environment & region
// ---------------------------------------------------------------------------
export function runClientEnvCheck(appBuild: string): CheckResult {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
  const details: CheckDetail[] = [
    { label: 'App build', value: appBuild },
    { label: 'UTC time', value: new Date().toISOString() },
    { label: 'User agent', value: navigator.userAgent },
    { label: 'Platform', value: navigator.platform },
    { label: 'Language', value: navigator.language },
    { label: 'Timezone', value: timeZone },
    { label: 'Screen', value: `${window.screen.width}x${window.screen.height}` },
  ];
  return {
    id: 'client-env',
    title: 'Client environment & region',
    status: 'pass',
    summary: `build ${appBuild}, timezone ${timeZone}`,
    timings: [],
    details,
  };
}

// ---------------------------------------------------------------------------
// 2. Auth (READ-ONLY)
// ---------------------------------------------------------------------------
// Reports the access token's presence and expiry by reading it locally. It must
// NOT call ensureImmersiveSession / any re-exchange: that rotates the app token
// and forces the live socket to reconnect - a diagnostic must not mutate auth
// state (IM-2524).
const decodeJwtExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json?.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
};

export function runAuthCheck(accessToken: string | null): CheckResult {
  const hasToken = !!accessToken;
  const details: CheckDetail[] = [
    { label: 'Access token', value: hasToken ? 'present (redacted)' : 'absent' },
  ];
  if (!hasToken) {
    return {
      id: 'auth',
      title: 'Auth',
      status: 'fail',
      summary: 'no access token in app state',
      timings: [],
      details,
    };
  }
  const exp = decodeJwtExp(accessToken as string);
  if (exp == null) {
    details.push({ label: 'Token expiry', value: 'not readable' });
    return {
      id: 'auth',
      title: 'Auth',
      status: 'warn',
      summary: 'access token present; expiry not readable',
      timings: [],
      details,
    };
  }
  const expMs = exp * 1000;
  const expired = expMs <= Date.now();
  const remainingMin = Math.round((expMs - Date.now()) / 60000);
  details.push({ label: 'Token expiry (UTC)', value: new Date(expMs).toISOString() });
  return {
    id: 'auth',
    title: 'Auth',
    status: expired ? 'warn' : 'pass',
    summary: expired
      ? 'access token present but EXPIRED'
      : `access token present; expires in ${remainingMin} min`,
    timings: [],
    details,
  };
}

// ---------------------------------------------------------------------------
// 3. WebSocket — configuration check only (READ-ONLY, non-disruptive)
// ---------------------------------------------------------------------------
// This must NOT open a socket. Opening a second authenticated socket to the same
// backend with the same token makes the backend treat it as a duplicate session
// and drop the app's live socket - which loses in-flight risk runs (they hang on
// "Calculating...") and triggers a reconnect/reload. A diagnostic has to be
// read-only, so we report configuration and token readiness only; WS host
// reachability is covered, non-disruptively, by the reachability check below
// (IM-2524).
export function runWebSocketCheck(token: string | null): CheckResult {
  const redactedUrl = WS_BASE_URL ? `${WS_BASE_URL}/ws?token=<redacted>` : '(not configured)';
  const details: CheckDetail[] = [
    { label: 'Socket URL', value: redactedUrl },
    {
      label: 'Auth token',
      value: token ? 'present (an authenticated socket can be opened)' : 'absent',
    },
    { label: 'Live probe', value: 'skipped - opening one would disrupt the app live session' },
  ];
  if (!WS_BASE_URL) {
    return {
      id: 'websocket',
      title: 'WebSocket',
      status: 'fail',
      summary: 'REACT_APP_WEBSOCKET_URL is not configured',
      timings: [],
      details,
    };
  }
  if (!token) {
    return {
      id: 'websocket',
      title: 'WebSocket',
      status: 'warn',
      summary: 'WebSocket configured, but no access token to authenticate a socket',
      timings: [],
      details,
    };
  }
  return {
    id: 'websocket',
    title: 'WebSocket',
    status: 'pass',
    summary: 'WebSocket configured; authenticated socket available (live probe skipped)',
    timings: [],
    details,
  };
}

// ---------------------------------------------------------------------------
// 4. REST API probe (IM-2522) — a real authenticated REST round-trip
// ---------------------------------------------------------------------------
// Confirms the REST API answers independently of the WebSocket. All backend
// functions except subscriptions are available over REST, so there are
// occasions where REST works and the socket will not open (Stephen). This
// probe calls a cheap, non-subscription, authenticated endpoint and reports
// reachable / status / round-trip, so the panel can tell "whole backend down"
// from "WebSocket transport down, REST fine".
const REST_SLOW_MS = 1500;

export async function runRestCheck(token: string | null): Promise<CheckResult> {
  const url = `${REST_BASE_URL}${REST_PROBE_PATH}`;
  const details: CheckDetail[] = [
    { label: 'Endpoint', value: `GET ${url}` },
    { label: 'Note', value: 'non-subscription functions are available over REST' },
  ];

  if (!token) {
    return {
      id: 'rest',
      title: 'REST API',
      status: 'fail',
      summary: 'no access token; cannot make an authenticated REST call',
      timings: [{ label: 'round-trip', ms: null }],
      details,
    };
  }

  const start = nowMs();
  let httpStatus = 'network-error';
  let httpCode = 0;
  let networkOk = false;
  let httpOk = false;
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    networkOk = true;
    httpOk = res.ok;
    httpCode = res.status;
    httpStatus = `HTTP ${res.status}`;
  } catch (err) {
    httpStatus = `error: ${errMessage(err)}`;
  }
  const ms = round1(nowMs() - start);

  // No response at all -> fail: the REST transport itself is down. A 5xx is also
  // a fail: REST answers but the server is erroring, which is a genuine REST
  // fault (must not read as healthy). A non-2xx below 500 (e.g. 401/403) -> warn:
  // REST is up, the call was rejected - the auth check covers that cause. A 2xx
  // -> pass, or warn when the round-trip is slow.
  let status: CheckStatus;
  let summary: string;
  if (!networkOk) {
    status = 'fail';
    summary = `REST call did not reach the server (${httpStatus})`;
  } else if (httpCode >= 500) {
    status = 'fail';
    summary = `REST server error (${httpStatus})`;
  } else if (!httpOk) {
    status = 'warn';
    summary = `REST reachable but returned ${httpStatus}`;
  } else if (ms >= REST_SLOW_MS) {
    status = 'warn';
    summary = `REST OK (${httpStatus}) but slow: ${ms} ms round-trip`;
  } else {
    status = 'pass';
    summary = `REST OK (${httpStatus}); ${ms} ms round-trip`;
  }

  return {
    id: 'rest',
    title: 'REST API',
    status,
    summary,
    timings: [{ label: 'round-trip', ms }],
    details: [...details, { label: 'Response', value: httpStatus }],
  };
}

// ---------------------------------------------------------------------------
// 5. Request-phase breakdown (PerformanceResourceTiming)
// ---------------------------------------------------------------------------
// Find this run's own resource-timing entry for `url`: the latest one that
// started at/after `since` (this function's own fetch) and has a readable
// responseStart. The checks run concurrently and the reachability probe hits
// the same URL with mode:'no-cors', whose entry reports responseStart 0 (opaque)
// and zeroed sub-phases; skipping responseStart === 0 binds the breakdown to its
// own CORS request instead of a sibling check's opaque one (IM-2523).
const ownResourceEntry = (url: string, since: number): PerformanceResourceTiming | null => {
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  let latest: PerformanceResourceTiming | null = null;
  for (const entry of entries) {
    if (
      entry.name === url &&
      entry.startTime >= since &&
      entry.responseStart > 0 &&
      (latest == null || entry.startTime > latest.startTime)
    ) {
      latest = entry;
    }
  }
  return latest;
};

// A phase duration, or null when either boundary is missing (0) — which is how
// a cross-origin entry without Timing-Allow-Origin reports its sub-phases.
const phase = (end: number, begin: number): number | null =>
  end > 0 && begin > 0 && end >= begin ? round1(end - begin) : null;

export async function runRequestPhaseCheck(token: string | null): Promise<CheckResult> {
  const url = `${REST_BASE_URL}${REST_PROBE_PATH}`;
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const start = nowMs();
  let httpStatus = 'network-error';
  let networkOk = false;
  try {
    const res = await fetch(url, { method: 'GET', headers, cache: 'no-store' });
    httpStatus = `HTTP ${res.status}`;
    networkOk = true;
  } catch (err) {
    httpStatus = `error: ${errMessage(err)}`;
  }
  const wallMs = round1(nowMs() - start);

  const entry = ownResourceEntry(url, start);
  const timings = entry
    ? [
        { label: 'DNS', ms: phase(entry.domainLookupEnd, entry.domainLookupStart) },
        { label: 'Connect', ms: phase(entry.connectEnd, entry.connectStart) },
        {
          label: 'TLS',
          ms:
            entry.secureConnectionStart > 0
              ? phase(entry.connectEnd, entry.secureConnectionStart)
              : null,
        },
        { label: 'Server (TTFB)', ms: phase(entry.responseStart, entry.requestStart) },
        { label: 'Transfer', ms: phase(entry.responseEnd, entry.responseStart) },
        { label: 'Wall clock', ms: wallMs },
      ]
    : [{ label: 'Wall clock', ms: wallMs }];

  const haveSubPhases = timings.some((t) => t.label !== 'Wall clock' && t.ms != null);
  // Reachability itself is covered by check 5; here a response (any status) with
  // readable phases is a pass. A response with no phase detail (cross-origin
  // without Timing-Allow-Origin) is a warn. A network failure is a fail.
  const status: CheckStatus = !networkOk ? 'fail' : haveSubPhases ? 'pass' : 'warn';

  return {
    id: 'request-phase',
    title: 'Request-phase breakdown',
    status,
    summary: entry
      ? `${httpStatus}; phases from PerformanceResourceTiming`
      : `${httpStatus}; no resource-timing entry (cross-origin Timing-Allow-Origin required)`,
    timings,
    details: [
      { label: 'Sample request', value: `GET ${url}` },
      { label: 'Response', value: httpStatus },
    ],
  };
}

// ---------------------------------------------------------------------------
// 6. Per-backend reachability (best-effort)
// ---------------------------------------------------------------------------
interface BackendTarget {
  label: string;
  url: string;
}

// WS base is probed over https (same host, no ws:// fetch): we only need to know
// the host answers, not to speak the WS protocol.
const wsBaseAsHttp = (wsBase: string): string =>
  wsBase.replace(/^wss:\/\//i, 'https://').replace(/^ws:\/\//i, 'http://');

// no-cors: the promise resolves on any server response (opaque) and only rejects
// on a true network/DNS failure, so it is a cleaner reachability signal than a
// cors fetch that a CORS policy would reject despite the host being up. We trade
// away the ability to read the status code — acceptable for reachability.
async function probeReachability(
  url: string,
): Promise<{ reachable: boolean; ms: number | null; note: string }> {
  const start = nowMs();
  try {
    await fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store' });
    return { reachable: true, ms: round1(nowMs() - start), note: 'responded (opaque, no-cors)' };
  } catch (err) {
    return { reachable: false, ms: round1(nowMs() - start), note: errMessage(err) };
  }
}

export async function runBackendReachabilityCheck(): Promise<CheckResult> {
  const targets: BackendTarget[] = [
    { label: 'REST backend', url: `${REST_BASE_URL}${REST_PROBE_PATH}` },
  ];
  if (WS_BASE_URL) {
    targets.push({ label: 'WS backend', url: wsBaseAsHttp(WS_BASE_URL) });
  }

  const probes = await Promise.all(targets.map((t) => probeReachability(t.url)));

  const timings = targets.map((t, i) => ({ label: t.label, ms: probes[i].ms }));
  const details = targets.map((t, i) => ({
    label: t.label,
    value: `${t.url} - ${probes[i].reachable ? 'reachable' : 'unreachable'} (${probes[i].note})`,
  }));

  const reachableCount = probes.filter((p) => p.reachable).length;
  const status: CheckStatus =
    reachableCount === targets.length ? 'pass' : reachableCount === 0 ? 'fail' : 'warn';

  return {
    id: 'reachability',
    title: 'Per-backend reachability',
    status,
    summary: `${reachableCount}/${targets.length} backend(s) reachable (best-effort; no /health yet)`,
    timings,
    details,
  };
}

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------
// Backstop timeout so no single check can hang the battery. The WebSocket check
// has its own 8s graceful timeout; this is the ceiling for the fetch-based
// checks (REST/request-phase/reachability/auth), which otherwise await a fetch
// with no timeout and would stall indefinitely against a backend that accepts
// the connection but never responds.
const CHECK_TIMEOUT_MS = 10000;

// Wrap a check so it can neither stall nor abort the others: if it does not
// settle within CHECK_TIMEOUT_MS it resolves a synthetic fail, and an
// unexpected throw is converted to a fail. `id`/`title` must match the check's
// own so the synthetic result slots into the same card (IM-2523).
async function guardCheck(
  id: string,
  title: string,
  run: () => Promise<CheckResult>,
): Promise<CheckResult> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<CheckResult>((resolve) => {
    timer = setTimeout(
      () =>
        resolve({
          id,
          title,
          status: 'fail',
          summary: `check did not complete within ${CHECK_TIMEOUT_MS / 1000}s (timed out)`,
          timings: [{ label: 'timeout', ms: CHECK_TIMEOUT_MS }],
          details: [
            {
              label: 'Note',
              value: 'the probe hung; the backend may be accepting connections but not responding',
            },
          ],
        }),
      CHECK_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([run(), timeout]);
  } catch (err) {
    return {
      id,
      title,
      status: 'fail',
      summary: `check errored: ${errMessage(err)}`,
      timings: [],
      details: [],
    };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function runAllDiagnostics(ctx: DiagnosticsContext): Promise<CheckResult[]> {
  // Client-env is synchronous and cannot hang. Every network check runs
  // concurrently and independently under guardCheck, so total run time is the
  // slowest check (capped by the timeout), not the sum, and a single hanging or
  // throwing probe can neither stall the others nor block the report. Array
  // order here is the display order (IM-2523).
  let clientEnv: CheckResult;
  try {
    clientEnv = runClientEnvCheck(ctx.appBuild);
  } catch (err) {
    clientEnv = {
      id: 'client-env',
      title: 'Client environment & region',
      status: 'fail',
      summary: `check errored: ${errMessage(err)}`,
      timings: [],
      details: [],
    };
  }
  const [auth, ws, rest, requestPhase, reachability] = await Promise.all([
    guardCheck('auth', 'Auth', async () => runAuthCheck(ctx.accessToken)),
    guardCheck('websocket', 'WebSocket', async () => runWebSocketCheck(ctx.accessToken)),
    guardCheck('rest', 'REST API', () => runRestCheck(ctx.accessToken)),
    guardCheck('request-phase', 'Request-phase breakdown', () =>
      runRequestPhaseCheck(ctx.accessToken),
    ),
    guardCheck('reachability', 'Per-backend reachability', () => runBackendReachabilityCheck()),
  ]);
  return [clientEnv, auth, ws, rest, requestPhase, reachability];
}

// ---------------------------------------------------------------------------
// Overall verdict (IM-2465)
// ---------------------------------------------------------------------------
// Deliberately simple and explainable: map the five checks to one plain-language
// cause so a user knows where to look first. Not a diagnosis, a triage pointer.
//
//  - "client-side (network/region)": the failing signals are local to the user.
//    A backend is unreachable (DNS/network reject), the connection-setup phases
//    (DNS + TCP connect + TLS) are slow, or the raw per-backend probe latency is
//    high -- i.e. the pipe is the problem.
//  - "backend": the server is reached but behaves badly -- auth cannot be
//    established, the WebSocket will not open, the REST API does not respond, or
//    the server's time-to-first-byte is slow while connection setup was fast.
//  - transport split: when exactly one of REST / WebSocket is down while the
//    other is healthy, say so specifically -- they are independent, and REST
//    carries every non-subscription call (IM-2522).
//  - "All checks healthy": every check passed.
//  - otherwise, if the only non-pass checks are warnings: no problems detected,
//    some diagnostics were just unavailable (defer to the individual cards).
//  - a failure with no matched client/backend reason stays flagged as such.
//
// When BOTH client and backend signals are present, the network cause is
// reported first: a network fault cascades into backend-looking symptoms, so
// it is the correct thing to rule out before blaming the server.
const NETWORK_SETUP_SLOW_MS = 600; // DNS + connect + TLS, summed
const SERVER_SLOW_MS = 1200; // time-to-first-byte
const BACKEND_PROBE_SLOW_MS = 1500; // per-backend reachability wall time

const timingMs = (result: CheckResult | undefined, label: string): number | null =>
  result?.timings.find((t) => t.label === label)?.ms ?? null;

export function deriveVerdict(results: CheckResult[]): Verdict {
  if (results.length === 0) {
    return { status: 'warn', headline: 'No checks have run yet.' };
  }
  if (results.every((r) => r.status === 'pass')) {
    return { status: 'pass', headline: 'All checks healthy.' };
  }

  const byId = (id: string): CheckResult | undefined => results.find((r) => r.id === id);
  const worst: CheckStatus = results.some((r) => r.status === 'fail')
    ? 'fail'
    : results.some((r) => r.status === 'warn')
      ? 'warn'
      : 'pass';

  const phaseCheck = byId('request-phase');
  const setupPhases = ['DNS', 'Connect', 'TLS']
    .map((label) => timingMs(phaseCheck, label))
    .filter((ms): ms is number => ms != null);
  const setupSlow =
    setupPhases.length > 0 && setupPhases.reduce((a, b) => a + b, 0) >= NETWORK_SETUP_SLOW_MS;
  const ttfb = timingMs(phaseCheck, 'Server (TTFB)');
  const serverSlow = ttfb != null && ttfb >= SERVER_SLOW_MS;

  const reachCheck = byId('reachability');
  const anyUnreachable = reachCheck != null && reachCheck.status !== 'pass';
  const reachSlow = (reachCheck?.timings ?? []).some(
    (t) => t.ms != null && t.ms >= BACKEND_PROBE_SLOW_MS,
  );

  const authBad = (byId('auth')?.status ?? 'pass') !== 'pass';
  // Asymmetric on purpose: wsBad is "not healthy" (warn or fail), but restBad is
  // "failed" only. The transport split below calls the OTHER transport healthy,
  // so a merely degraded (warn) REST must not count as "down" - otherwise the
  // split would wrongly assert the WebSocket is fine. Do not normalise these.
  const wsBad = (byId('websocket')?.status ?? 'pass') !== 'pass';
  const wsOk = (byId('websocket')?.status ?? 'pass') === 'pass';
  const restCheck = byId('rest');
  const restBad = restCheck != null && restCheck.status === 'fail';
  const restOk = (restCheck?.status ?? 'pass') === 'pass';

  const clientReasons: string[] = [];
  if (anyUnreachable) clientReasons.push('a backend is unreachable');
  if (setupSlow) clientReasons.push('slow DNS/connect/TLS setup');
  if (reachSlow) clientReasons.push('high per-backend latency');

  const backendReasons: string[] = [];
  if (authBad) backendReasons.push('auth not established');
  if (wsBad) backendReasons.push('WebSocket will not open');
  if (restBad) backendReasons.push('REST API not responding');
  if (serverSlow) backendReasons.push('slow server response (TTFB)');

  if (clientReasons.length > 0) {
    return {
      status: worst,
      headline: `Likely client-side (network/region): ${clientReasons.join('; ')}.`,
    };
  }
  // Transport split: REST and the WebSocket are independent, so one can be down
  // while the other is healthy. Only claim the split when the down transport is
  // the ONLY backend problem - otherwise a concurrent auth/TTFB fault would be
  // dropped and the "other is healthy" headline would mislead; in that case fall
  // through to the generic message, which lists every backend reason. (A truly
  // unreachable REST host also trips clientReasons above via the reachability
  // probe, so restBad && wsOk here is mainly the CORS / cors-fetch-rejected case.)
  const otherBackendProblem = authBad || serverSlow;
  if (wsBad && restOk && !otherBackendProblem) {
    return {
      status: worst,
      headline:
        'WebSocket transport is down but REST is healthy - live updates/subscriptions will not work; request/response calls should.',
    };
  }
  if (restBad && wsOk && !otherBackendProblem) {
    return {
      status: worst,
      headline:
        'REST is down but the WebSocket is healthy - request/response calls will fail while the live feed works.',
    };
  }
  if (backendReasons.length > 0) {
    return { status: worst, headline: `Likely backend: ${backendReasons.join('; ')}.` };
  }
  // No client- or backend-fault signal fired. If the only non-pass checks are
  // warnings, they are informational (e.g. a missing Timing-Allow-Origin header
  // hides per-phase timing) and the connection is healthy. A failure with no
  // matched reason is still a genuine problem, so keep it cautious.
  if (worst === 'fail') {
    return { status: 'fail', headline: 'Some checks failed - review the individual checks below.' };
  }
  return {
    status: worst,
    headline: 'No problems detected; some diagnostics were unavailable - see the checks below.',
  };
}
